/**
 * PWA environment detection utilities.
 *
 * Centralises all iOS / standalone detection in one place so that the rest of
 * the codebase doesn't duplicate these checks.
 */

/**
 * Returns true for iPhone, iPad (including modern iPad Pros which report
 * platform 'MacIntel' with touch support), and iPod.
 */
export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    // iPad Pro: reports 'MacIntel' but has touch points
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

/**
 * Returns true when the app is running in standalone (installed PWA) mode.
 * Works for both iOS Safari (navigator.standalone) and other browsers
 * (display-mode: standalone media query).
 */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as { standalone?: boolean }).standalone === true
  );
}

/**
 * Returns true only when running as an installed PWA on iOS/iPadOS.
 * Use this to activate iOS-specific resilience behaviours (dual storage,
 * aggressive sync, anti-eviction heartbeat, etc.).
 */
export function isIOSPWA(): boolean {
  return isIOS() && isStandalone();
}
